import { useEffect, useState } from 'react'
import { supabase } from '../../lib/supabase'
import DashboardLayout from '../../components/dashboard/DashboardLayout'
import PageHeader from '../../components/dashboard/PageHeader'
import Card from '../../components/dashboard/Card'
import LoadingSpinner from '../../components/LoadingSpinner'
import toast from 'react-hot-toast'
import { 
  Buildings, 
  User, 
  Lock,
  Eye,
  EyeSlash,
  UserGear,
  Plus,
  Trash,
  Crown
} from '@phosphor-icons/react'

export default function SettingsPage() {
  const [activeTab, setActiveTab] = useState('organization')
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  
  // User data
  const [user, setUser] = useState(null)
  const [organization, setOrganization] = useState(null)
  const [isOwner, setIsOwner] = useState(false)
  
  // Admin management
  const [admins, setAdmins] = useState([])
  const [members, setMembers] = useState([])
  const [showAddAdminModal, setShowAddAdminModal] = useState(false)
  const [selectedMember, setSelectedMember] = useState('')
  const [makingOwner, setMakingOwner] = useState(false)
  
  // Organization form
  const [orgForm, setOrgForm] = useState({
    name: '',
    slug: '',
    logo_url: ''
  })
  const [uploadingLogo, setUploadingLogo] = useState(false)
  
  // User profile form
  const [profileForm, setProfileForm] = useState({
    name: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    state: '',
    country: '',
    post_code: '',
    date_of_birth: '',
    profile_photo_url: '',
    avatar_url: ''
  })
  const [uploadingAvatar, setUploadingAvatar] = useState(false)
  
  // Password form
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  })
  const [showCurrentPassword, setShowCurrentPassword] = useState(false)
  const [showNewPassword, setShowNewPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [passwordError, setPasswordError] = useState('')

  useEffect(() => {
    loadData()
  }, [])

  const loadData = async () => {
    setLoading(true)
    try {
      const { data: { user: authUser } } = await supabase.auth.getUser()
      if (!authUser) return

      // Load user data
      const { data: userData } = await supabase
        .from('users')
        .select('*')
        .eq('id', authUser.id)
        .single()

      setUser(userData)
      setProfileForm({
        name: userData.name || '',
        email: userData.email || '',
        phone: userData.phone || '',
        address: userData.address || '',
        city: userData.city || '',
        state: userData.state || '',
        country: userData.country || '',
        post_code: userData.post_code || '',
        date_of_birth: userData.date_of_birth || '',
        profile_photo_url: userData.profile_photo_url || '',
        avatar_url: userData.avatar_url || ''
      })

      // Load organization
      if (userData.org_id) {
        const { data: orgData } = await supabase
          .from('organizations')
          .select('*')
          .eq('id', userData.org_id)
          .single()

        setOrganization(orgData)
        setOrgForm({
          name: orgData.name || '',
          slug: orgData.slug || '',
          logo_url: orgData.logo_url || ''
        })

        // Check if user is owner admin
        const { data: adminData, error: adminError } = await supabase
          .from('organization_admins')
          .select('is_owner')
          .eq('user_id', authUser.id)
          .eq('org_id', userData.org_id)
          .maybeSingle()

        if (adminData?.is_owner) {
          setIsOwner(true)
          await loadAdmins(userData.org_id)
        } else {
          // User is not owner - hide Admins tab
          setIsOwner(false)
        }
      }
    } catch (error) {
      console.error('Load data error:', error)
      toast.error('Failed to load settings')
    } finally {
      setLoading(false)
    }
  }

  const loadAdmins = async (orgId) => {
    try {
      // Load all admins
      const { data: adminsData } = await supabase
        .from('organization_admins')
        .select(`
          id,
          is_owner,
          users!inner (
            id,
            name,
            email,
            profile_photo_url,
            avatar_url
          )
        `)
        .eq('org_id', orgId)

      setAdmins(adminsData || [])

      // Load non-admin members for adding new admins
      const adminUserIds = adminsData?.map(a => a.users.id) || []
      const { data: membersData } = await supabase
        .from('users')
        .select('id, name, email')
        .eq('org_id', orgId)
        .not('id', 'in', `(${adminUserIds.join(',')})`)

      setMembers(membersData || [])
    } catch (error) {
      console.error('Load admins error:', error)
      toast.error('Failed to load admins')
    }
  }

  const handleAddAdmin = async () => {
    if (!selectedMember) {
      toast.error('Please select a member')
      return
    }

    setSaving(true)
    try {
      const { error } = await supabase
        .from('organization_admins')
        .insert({
          org_id: organization.id,
          user_id: selectedMember,
          is_owner: false
        })

      if (error) throw error

      toast.success('Admin added successfully')
      setShowAddAdminModal(false)
      setSelectedMember('')
      await loadAdmins(organization.id)
    } catch (error) {
      console.error('Add admin error:', error)
      toast.error('Failed to add admin')
    } finally {
      setSaving(false)
    }
  }

  const handleRemoveAdmin = async (adminId, userId) => {
    if (!confirm('Are you sure you want to remove this admin?')) return

    setSaving(true)
    try {
      const { error } = await supabase
        .from('organization_admins')
        .delete()
        .eq('id', adminId)

      if (error) throw error

      toast.success('Admin removed successfully')
      await loadAdmins(organization.id)
    } catch (error) {
      console.error('Remove admin error:', error)
      toast.error('Failed to remove admin')
    } finally {
      setSaving(false)
    }
  }

  const handleMakeOwner = async (adminId, userId) => {
    if (!confirm('Transfer ownership to this admin? You will become a regular admin.')) return

    setMakingOwner(true)
    try {
      // Call atomic transfer function (single transaction, no constraint violation)
      const { data, error } = await supabase.rpc('transfer_org_ownership', {
        p_org_id: organization.id,
        p_old_owner_id: user.id,
        p_new_owner_admin_id: adminId
      })

      if (error) throw error

      // Get the new owner's info for the message
      const newOwner = admins.find(a => a.id === adminId)
      
      toast.success(`Ownership transferred to ${newOwner?.users?.name}. They must refresh their page.`)
      
      // Switch to organization tab before losing access to admins tab
      setActiveTab('organization')
      
      // Reload all data to update ownership state
      await loadData()
      
    } catch (error) {
      console.error('Transfer ownership error:', error)
      toast.error(error.message || 'Failed to transfer ownership')
    } finally {
      setMakingOwner(false)
    }
  }

  const handleUpdateOrganization = async (e) => {
    e.preventDefault()
    setSaving(true)
    try {
      const { error } = await supabase
        .from('organizations')
        .update({
          name: orgForm.name,
          slug: orgForm.slug
        })
        .eq('id', organization.id)

      if (error) throw error

      toast.success('Organization updated successfully')
      loadData()
    } catch (error) {
      console.error('Update organization error:', error)
      toast.error('Failed to update organization')
    } finally {
      setSaving(false)
    }
  }

  const handleLogoUpload = async (e) => {
    const file = e.target.files[0]
    if (!file) return

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please upload an image file')
      return
    }

    // Validate file size (max 2MB)
    if (file.size > 2 * 1024 * 1024) {
      toast.error('Image must be less than 2MB')
      return
    }

    setUploadingLogo(true)
    try {
      // Upload to Supabase storage
      const fileExt = file.name.split('.').pop()
      const fileName = `${organization.id}-${Date.now()}.${fileExt}`
      const filePath = `${fileName}`

      const { error: uploadError } = await supabase.storage
        .from('wallet-assets')
        .upload(filePath, file)

      if (uploadError) throw uploadError

      // Get public URL
      const { data: { publicUrl } } = supabase.storage
        .from('wallet-assets')
        .getPublicUrl(filePath)

      // Update organization with new logo URL
      const { error: updateError } = await supabase
        .from('organizations')
        .update({ logo_url: publicUrl })
        .eq('id', organization.id)

      if (updateError) throw updateError

      toast.success('Logo uploaded successfully')
      loadData()
    } catch (error) {
      console.error('Logo upload error:', error)
      toast.error('Failed to upload logo')
    } finally {
      setUploadingLogo(false)
    }
  }

  const handleRemoveLogo = async () => {
    if (!confirm('Remove organization logo?')) return

    setSaving(true)
    try {
      const { error } = await supabase
        .from('organizations')
        .update({ logo_url: null })
        .eq('id', organization.id)

      if (error) throw error

      toast.success('Logo removed')
      loadData()
    } catch (error) {
      console.error('Remove logo error:', error)
      toast.error('Failed to remove logo')
    } finally {
      setSaving(false)
    }
  }

  const handleUpdateProfile = async (e) => {
    e.preventDefault()
    setSaving(true)
    try {
      const { error } = await supabase
        .from('users')
        .update({
          name: profileForm.name,
          phone: profileForm.phone,
          address: profileForm.address,
          city: profileForm.city,
          state: profileForm.state,
          country: profileForm.country,
          post_code: profileForm.post_code,
          date_of_birth: profileForm.date_of_birth || null // Convert empty string to null
        })
        .eq('id', user.id)

      if (error) throw error

      toast.success('Profile updated successfully')
      loadData()
    } catch (error) {
      console.error('Update profile error:', error)
      toast.error('Failed to update profile')
    } finally {
      setSaving(false)
    }
  }

  const handleAvatarUpload = async (e) => {
    const file = e.target.files[0]
    if (!file) return

    if (!file.type.startsWith('image/')) {
      toast.error('Please upload an image file')
      return
    }

    if (file.size > 2 * 1024 * 1024) {
      toast.error('Image must be less than 2MB')
      return
    }

    setUploadingAvatar(true)
    try {
      const fileExt = file.name.split('.').pop()
      const fileName = `${user.id}-${Date.now()}.${fileExt}`
      const filePath = `${fileName}`

      const { error: uploadError } = await supabase.storage
        .from('user-uploads')
        .upload(filePath, file)

      if (uploadError) throw uploadError

      const { data: { publicUrl } } = supabase.storage
        .from('user-uploads')
        .getPublicUrl(filePath)

      const { error: updateError } = await supabase
        .from('users')
        .update({ profile_photo_url: publicUrl })
        .eq('id', user.id)

      if (updateError) throw updateError

      toast.success('Avatar uploaded successfully')
      loadData()
    } catch (error) {
      console.error('Avatar upload error:', error)
      toast.error('Failed to upload avatar')
    } finally {
      setUploadingAvatar(false)
    }
  }

  const handleUpdatePassword = async (e) => {
    e.preventDefault()
    setPasswordError('')

    // Validate current password is provided
    if (!passwordForm.currentPassword) {
      setPasswordError('Current password is required')
      toast.error('Current password is required')
      return
    }

    // Validate new password length
    if (passwordForm.newPassword.length < 8) {
      setPasswordError('Password must be at least 8 characters')
      toast.error('Password must be at least 8 characters')
      return
    }

    // Validate passwords match
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      setPasswordError('New passwords do not match')
      toast.error('New passwords do not match')
      return
    }

    // Check if new password is different from current
    if (passwordForm.currentPassword === passwordForm.newPassword) {
      setPasswordError('New password must be different from current password')
      toast.error('New password must be different from current password')
      return
    }

    setSaving(true)
    try {
      // First, verify current password by attempting to sign in
      const { data: { user: authUser } } = await supabase.auth.getUser()
      
      const { error: verifyError } = await supabase.auth.signInWithPassword({
        email: authUser.email,
        password: passwordForm.currentPassword
      })

      if (verifyError) {
        setPasswordError('Current password is incorrect')
        toast.error('Current password is incorrect')
        setSaving(false)
        return
      }

      // If verification passed, update password
      const { error } = await supabase.auth.updateUser({
        password: passwordForm.newPassword
      })

      if (error) throw error

      toast.success('Password updated successfully')
      setPasswordForm({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      })
      setPasswordError('')
    } catch (error) {
      console.error('Update password error:', error)
      setPasswordError(error.message || 'Failed to update password')
      toast.error(error.message || 'Failed to update password')
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return <LoadingSpinner message="Loading settings..." />
  }


  const tabs = [
    { id: 'organization', label: 'Organization', icon: Buildings },
    { id: 'profile', label: 'Profile', icon: User },
    { id: 'security', label: 'Security', icon: Lock },
    ...(isOwner ? [{ id: 'admins', label: 'Admins', icon: UserGear }] : [])
  ]


  return (
    <DashboardLayout>
      <PageHeader
        title="Settings"
        description="Manage your organization and account settings"
      />

      <div className="p-6">
        {/* Tabs */}
        <Card className="mb-6">
          <div className="flex gap-2 border-b border-gray-200 overflow-x-auto">
            {tabs.map((tab) => {
              const Icon = tab.icon
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                    activeTab === tab.id
                      ? 'border-blue-600 text-blue-600'
                      : 'border-transparent text-gray-600 hover:text-gray-900'
                  }`}
                >
                  <Icon size={18} weight={activeTab === tab.id ? 'fill' : 'light'} />
                  {tab.label}
                </button>
              )
            })}
          </div>
        </Card>

        {/* Organization Settings */}
        {activeTab === 'organization' && (
          <div className="grid gap-6">
            <Card title="Organization Details">
              <form onSubmit={handleUpdateOrganization} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Organization Name *
                  </label>
                  <input
                    type="text"
                    required
                    value={orgForm.name}
                    onChange={(e) => setOrgForm({ ...orgForm, name: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Organization Slug *
                  </label>
                  <input
                    type="text"
                    required
                    value={orgForm.slug}
                    onChange={(e) => setOrgForm({ ...orgForm, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-') })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="my-organization"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    URL-friendly identifier (lowercase, hyphens only)
                  </p>
                </div>

                <button
                  type="submit"
                  disabled={saving}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors disabled:opacity-50"
                >
                  {saving ? 'Saving...' : 'Save Changes'}
                </button>
              </form>
            </Card>

            <Card title="Organization Logo">
              <div className="space-y-4">
                {organization?.logo_url && (
                  <div className="flex items-start gap-4">
                    <img
                      src={organization.logo_url}
                      alt="Organization logo"
                      className="w-24 h-24 rounded-lg object-cover border border-gray-200"
                    />
                    <button
                      onClick={handleRemoveLogo}
                      disabled={saving}
                      className="px-3 py-2 text-sm text-red-600 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50"
                    >
                      Remove Logo
                    </button>
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {organization?.logo_url ? 'Change Logo' : 'Upload Logo'}
                  </label>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleLogoUpload}
                    disabled={uploadingLogo}
                    className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-blue-50 file:text-blue-600 hover:file:bg-blue-100"
                  />
                  <p className="text-xs text-gray-500 mt-1">PNG, JPG up to 2MB</p>
                </div>
              </div>
            </Card>
          </div>
        )}

        {/* User Profile */}
        {activeTab === 'profile' && (
          <div className="grid gap-6">
            <Card title="Profile Information">
              <form onSubmit={handleUpdateProfile} className="space-y-4">
                {/* Basic Information */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Full Name *
                    </label>
                    <input
                      type="text"
                      required
                      value={profileForm.name}
                      onChange={(e) => setProfileForm({ ...profileForm, name: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Email Address
                    </label>
                    <input
                      type="email"
                      value={profileForm.email}
                      disabled
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-500 cursor-not-allowed"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Phone Number
                    </label>
                    <input
                      type="tel"
                      value={profileForm.phone}
                      onChange={(e) => setProfileForm({ ...profileForm, phone: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Date of Birth
                    </label>
                    <input
                      type="date"
                      value={profileForm.date_of_birth}
                      onChange={(e) => setProfileForm({ ...profileForm, date_of_birth: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>

                {/* Address Information */}
                <div className="pt-4 border-t border-gray-200">
                  <h3 className="text-sm font-semibold text-gray-900 mb-4">Address Information</h3>
                  <div className="grid grid-cols-1 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Street Address
                      </label>
                      <input
                        type="text"
                        value={profileForm.address}
                        onChange={(e) => setProfileForm({ ...profileForm, address: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          City
                        </label>
                        <input
                          type="text"
                          value={profileForm.city}
                          onChange={(e) => setProfileForm({ ...profileForm, city: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          State/Province
                        </label>
                        <input
                          type="text"
                          value={profileForm.state}
                          onChange={(e) => setProfileForm({ ...profileForm, state: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Country
                        </label>
                        <input
                          type="text"
                          value={profileForm.country}
                          onChange={(e) => setProfileForm({ ...profileForm, country: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Postal Code
                        </label>
                        <input
                          type="text"
                          value={profileForm.post_code}
                          onChange={(e) => setProfileForm({ ...profileForm, post_code: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={saving}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors disabled:opacity-50"
                >
                  {saving ? 'Saving...' : 'Save Changes'}
                </button>
              </form>
            </Card>

            <Card title="Profile Photo">
              <div className="space-y-4">
                {(user?.profile_photo_url || user?.avatar_url) && (
                  <div className="flex items-start gap-4">
                    <img
                      src={user.profile_photo_url || user.avatar_url}
                      alt="Profile"
                      className="w-24 h-24 rounded-full object-cover border border-gray-200"
                    />
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {(user?.profile_photo_url || user?.avatar_url) ? 'Change Photo' : 'Upload Photo'}
                  </label>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleAvatarUpload}
                    disabled={uploadingAvatar}
                    className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-blue-50 file:text-blue-600 hover:file:bg-blue-100"
                  />
                  <p className="text-xs text-gray-500 mt-1">PNG, JPG up to 2MB</p>
                </div>
              </div>
            </Card>
          </div>
        )}

        {/* Security */}
        {activeTab === 'security' && (
          <Card title="Change Password">
            <form onSubmit={handleUpdatePassword} className="space-y-4 max-w-md">
              {passwordError && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
                  {passwordError}
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Current Password *
                </label>
                <div className="relative">
                  <input
                    type={showCurrentPassword ? 'text' : 'password'}
                    required
                    value={passwordForm.currentPassword}
                    onChange={(e) => {
                      setPasswordForm({ ...passwordForm, currentPassword: e.target.value })
                      setPasswordError('')
                    }}
                    className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <button
                    type="button"
                    onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
                  >
                    {showCurrentPassword ? <EyeSlash size={20} /> : <Eye size={20} />}
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  New Password *
                </label>
                <div className="relative">
                  <input
                    type={showNewPassword ? 'text' : 'password'}
                    required
                    value={passwordForm.newPassword}
                    onChange={(e) => {
                      setPasswordForm({ ...passwordForm, newPassword: e.target.value })
                      setPasswordError('')
                    }}
                    className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <button
                    type="button"
                    onClick={() => setShowNewPassword(!showNewPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
                  >
                    {showNewPassword ? <EyeSlash size={20} /> : <Eye size={20} />}
                  </button>
                </div>
                <p className="text-xs text-gray-500 mt-1">Minimum 8 characters</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Confirm New Password *
                </label>
                <div className="relative">
                  <input
                    type={showConfirmPassword ? 'text' : 'password'}
                    required
                    value={passwordForm.confirmPassword}
                    onChange={(e) => {
                      setPasswordForm({ ...passwordForm, confirmPassword: e.target.value })
                      // Real-time validation for matching passwords
                      if (passwordForm.newPassword && e.target.value && passwordForm.newPassword !== e.target.value) {
                        setPasswordError('Passwords do not match')
                      } else {
                        setPasswordError('')
                      }
                    }}
                    className={`w-full px-3 py-2 pr-10 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      passwordError === 'Passwords do not match' ? 'border-red-300' : 'border-gray-300'
                    }`}
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
                  >
                    {showConfirmPassword ? <EyeSlash size={20} /> : <Eye size={20} />}
                  </button>
                </div>
                {passwordForm.confirmPassword && passwordForm.newPassword !== passwordForm.confirmPassword && (
                  <p className="text-xs text-red-600 mt-1">Passwords do not match</p>
                )}
              </div>

              <button
                type="submit"
                disabled={saving || (passwordForm.confirmPassword && passwordForm.newPassword !== passwordForm.confirmPassword)}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {saving ? 'Updating...' : 'Update Password'}
              </button>
            </form>
          </Card>
        )}

        {/* Admins Management */}
        {activeTab === 'admins' && isOwner && (
          <div className="grid gap-6">
            {/* Header with Add Button */}
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Organization Administrators</h2>
                <p className="text-sm text-gray-500 mt-1">Manage who can administer your organization</p>
              </div>
              <button
                onClick={() => setShowAddAdminModal(true)}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors"
              >
                <Plus size={20} weight="bold" />
                Add Admin
              </button>
            </div>

            <Card>
              <div className="space-y-4">
                {admins.length === 0 ? (
                  <p className="text-gray-500 text-center py-8">No administrators yet</p>
                ) : (
                  <div className="space-y-3">
                    {admins.map((admin) => (
                      <div
                        key={admin.id}
                        className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                      >
                        <div className="flex items-center gap-3">
                          {admin.users.profile_photo_url || admin.users.avatar_url ? (
                            <img
                              src={admin.users.profile_photo_url || admin.users.avatar_url}
                              alt={admin.users.name}
                              className="w-12 h-12 rounded-full object-cover"
                            />
                          ) : (
                            <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center">
                              <User size={24} className="text-blue-600" />
                            </div>
                          )}
                          <div>
                            <div className="flex items-center gap-2">
                              <p className="font-medium text-gray-900">{admin.users.name}</p>
                              {admin.is_owner && (
                                <span className="flex items-center gap-1 px-2 py-1 bg-yellow-100 text-yellow-800 text-xs font-medium rounded-full">
                                  <Crown size={14} weight="fill" />
                                  Owner
                                </span>
                              )}
                              {admin.users.id === user.id && (
                                <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs font-medium rounded-full">
                                  You
                                </span>
                              )}
                            </div>
                            <p className="text-sm text-gray-500">{admin.users.email}</p>
                          </div>
                        </div>

                        <div className="flex items-center gap-2">
                          {!admin.is_owner && admin.users.id !== user.id && (
                            <>
                              <button
                                onClick={() => handleMakeOwner(admin.id, admin.users.id)}
                                disabled={makingOwner}
                                className="px-3 py-2 text-sm font-medium text-yellow-600 hover:bg-yellow-50 rounded-lg transition-colors disabled:opacity-50"
                                title="Transfer ownership to this admin"
                              >
                                <Crown size={18} weight="fill" />
                              </button>
                              <button
                                onClick={() => handleRemoveAdmin(admin.id, admin.users.id)}
                                disabled={saving}
                                className="px-3 py-2 text-sm font-medium text-red-600 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50"
                                title="Remove admin access"
                              >
                                <Trash size={18} />
                              </button>
                            </>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </Card>

            <Card title="About Admin Roles">
              <div className="space-y-3 text-sm text-gray-600">
                <p>
                  <strong className="text-gray-900">Owner Admin:</strong> Has full control over the organization including managing other admins, transferring ownership, and all standard admin capabilities.
                </p>
                <p>
                  <strong className="text-gray-900">Regular Admin:</strong> Can manage members, events, and organizational settings but cannot add/remove other admins.
                </p>
                <p className="text-yellow-800 bg-yellow-50 p-3 rounded-lg">
                  <strong>⚠️ Important:</strong> When you transfer ownership to another admin, you will become a regular admin and lose the ability to manage administrators. The new owner must refresh their page to see the Admins tab.
                </p>
              </div>
            </Card>
          </div>
        )}

        {/* Add Admin Modal */}
        {showAddAdminModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Add Administrator</h3>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Select Member
                  </label>
                  <select
                    value={selectedMember}
                    onChange={(e) => setSelectedMember(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Choose a member...</option>
                    {members.map((member) => (
                      <option key={member.id} value={member.id}>
                        {member.name} ({member.email})
                      </option>
                    ))}
                  </select>
                  {members.length === 0 && (
                    <p className="text-xs text-gray-500 mt-1">
                      All members are already administrators
                    </p>
                  )}
                </div>

                <div className="flex gap-3">
                  <button
                    onClick={() => {
                      setShowAddAdminModal(false)
                      setSelectedMember('')
                    }}
                    className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleAddAdmin}
                    disabled={!selectedMember || saving}
                    className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {saving ? 'Adding...' : 'Add Admin'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

      </div>
    </DashboardLayout>
  )
}